import { getValidAccessToken, refreshAccessToken } from "../utils/authRefresh";

// V2 SSE client: forwards chunks to `onChunk` inline without the queue/drain-timer pacing used by the
// v1 client. The v2 learning flow paces at the component layer via `useStreamBuffer`, so service-side
// pacing would double-buffer and delay the visible tokens.

type StreamHandlers<TPayload> = {
  onChunk?: (event: TPayload) => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
};

type CreateGraphqlSseClientArgs<TPayload, TVariables extends Record<string, unknown>> = {
  operationName: string;
  query: string;
  getPayload: (parsed: { data?: Record<string, unknown> }) => TPayload | undefined;
  logPrefix: string;
};

const GRAPHQL_SSE_ENDPOINT =
  typeof window === "undefined" ? "/api/graphql/stream" : `${window.location.origin}/api/graphql/stream`;

const parseSseEvent = (eventChunk: string): { eventName: string; data?: string } => {
  const lines = eventChunk.split("\n");
  let eventName = "message";
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim());
    }
  }

  return {
    eventName,
    data: dataLines.length ? dataLines.join("\n") : undefined,
  };
};

export const createGraphqlSseClientV2 = <TPayload, TVariables extends Record<string, unknown>>(
  config: CreateGraphqlSseClientArgs<TPayload, TVariables>
) => {
  let activeController: AbortController | null = null;

  const cancelStream = () => {
    if (activeController) {
      activeController.abort();
      activeController = null;
    }
  };

  const startStream = async (
    variables: TVariables,
    controller: AbortController,
    handlers: StreamHandlers<TPayload> = {}
  ) => {
    cancelStream();
    activeController = controller;

    let onCompleteCalled = false;

    const complete = () => {
      if (!onCompleteCalled) {
        onCompleteCalled = true;
        handlers.onComplete?.();
      }
    };

    try {
      const fetchWithAuth = async () => {
        let accessToken = await getValidAccessToken({ minTtlSeconds: 30 });
        if (!accessToken) {
          throw new Error(`No access token available for ${config.logPrefix} GraphQL SSE stream.`);
        }

        let response = await fetch(GRAPHQL_SSE_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            operationName: config.operationName,
            query: config.query,
            variables,
          }),
          signal: controller.signal,
        });

        if (response.status === 401 || response.status === 403) {
          const refreshed = await refreshAccessToken();
          if (refreshed?.accessToken) {
            accessToken = refreshed.accessToken;
            response = await fetch(GRAPHQL_SSE_ENDPOINT, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "text/event-stream",
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                operationName: config.operationName,
                query: config.query,
                variables,
              }),
              signal: controller.signal,
            });
          }
        }

        return response;
      };

      const response = await fetchWithAuth();

      if (!response.ok) {
        throw new Error(`Unable to start ${config.logPrefix} GraphQL SSE stream.`);
      }

      if (!response.body) {
        throw new Error("Server provided no stream body.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          complete();
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        let boundary = buffer.indexOf("\n\n");
        while (boundary !== -1) {
          const eventChunk = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);

          const { eventName, data } = parseSseEvent(eventChunk);

          // GraphQL SSE keep-alive frames come through as comment-only "message" events (":\n\n")
          if (eventName === "message" && !data) {
            boundary = buffer.indexOf("\n\n");
            continue;
          }

          // GraphQL SSE uses "next" for data events, "complete" for completion, "error" for errors
          if (eventName === "complete") {
            complete();
            cancelStream();
            return;
          }

          if (eventName === "error") {
            try {
              const errorData = data ? JSON.parse(data) : {};
              const error = new Error(errorData.message || "Stream error");
              handlers.onError?.(error);
            } catch (err) {
              handlers.onError?.(err as Error);
            }
            return;
          }

          // Handle "next" events (data events in GraphQL SSE)
          if (eventName === "next" && data) {
            try {
              const parsed = JSON.parse(data);

              if (parsed?.errors?.length) {
                const combinedErrorMessage = parsed.errors
                  .map((err: { message?: string }) => err.message)
                  .filter(Boolean)
                  .join(" | ");
                const error = new Error(combinedErrorMessage || "GraphQL stream error");
                console.error(`[${config.logPrefix}] GraphQL errors in next event`, {
                  combinedErrorMessage,
                  errorsCount: parsed.errors.length,
                });
                handlers.onError?.(error);
                boundary = buffer.indexOf("\n\n");
                continue;
              }
              const payload = config.getPayload(parsed);
              if (payload) {
                try {
                  handlers.onChunk?.(payload);
                } catch (handlerErr) {
                  console.error(`[${config.logPrefix}] Error in onChunk handler`, {
                    error: (handlerErr as Error).message,
                  });
                }
              }
            } catch (err) {
              console.error(`[${config.logPrefix}] Error parsing next event`, {
                err,
                errorMessage: (err as Error).message,
                data: data.substring(0, 500),
              });
              handlers.onError?.(err as Error);
            }
          }

          boundary = buffer.indexOf("\n\n");
        }
      }
    } catch (error) {
      if ((error as DOMException)?.name === "AbortError") {
        return;
      }
      handlers.onError?.(error as Error);
      throw error;
    } finally {
      if (activeController === controller) {
        activeController = null;
      }
    }
  };

  return { startStream, cancelStream };
};
